import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { 
  FaUser,
  FaCar,
  FaCheckCircle,
  FaExclamationCircle,
  FaEnvelope,
  FaPhone,
  FaHome,
  FaBriefcase,
  FaIdCard,
  FaPalette,
  FaTelegram
} from 'react-icons/fa';

const Signup = () => {
  const [role, setRole] = useState('rider'); // 'rider' or 'driver'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  // Rider form state
  const [riderForm, setRiderForm] = useState({
    email: '',
    name: '',
    phoneNumber: '',
    homeAddress: '',
    workAddress: '',
    acceptPrivacy: false
  });

  // Driver form state
  const [driverForm, setDriverForm] = useState({
    email: '',
    name: '',
    phoneNumber: '',
    licensePlateNumber: '',
    vehicleColour: '',
    vehicleModel: '',
    acceptPrivacy: false
  });

  const getApiBase = () => {
    return process.env.REACT_APP_MINIAPP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || '/telegramMiniApp';
  };

  const handleRiderChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRiderForm({
      ...riderForm,
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
  };

  const handleDriverChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDriverForm({
      ...driverForm,
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
  };

  const handleRiderSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Client-side validation
    if (!riderForm.email || !riderForm.name) {
      setError('Email and name are required');
      setIsSubmitting(false);
      return;
    }
    if (!riderForm.acceptPrivacy) {
      setError('You must accept the Privacy Policy and Terms');
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        email: riderForm.email.trim(),
        name: riderForm.name.trim(),
        phoneNumber: riderForm.phoneNumber.trim() || undefined,
        homeAddress: riderForm.homeAddress.trim() || undefined,
        workAddress: riderForm.workAddress.trim() || undefined,
        acceptPrivacy: true
      };
      
      const response = await axios.post(`${getApiBase()}/auth/signup-rider`, payload);
      
      if (response.data.success) {
        setIsSubmitted(true);
        setRiderForm({
          email: '',
          name: '',
          phoneNumber: '',
          homeAddress: '',
          workAddress: '',
          acceptPrivacy: false
        });
      } else {
        setError(response.data.error || 'Failed to create account');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create account. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDriverSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Client-side validation
    if (!driverForm.email || !driverForm.name || !driverForm.phoneNumber || 
        !driverForm.licensePlateNumber || !driverForm.vehicleColour) {
      setError('All required fields must be filled');
      setIsSubmitting(false);
      return;
    }
    if (!driverForm.acceptPrivacy) {
      setError('You must accept the Privacy Policy and Terms');
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = {
        email: driverForm.email.trim(),
        name: driverForm.name.trim(),
        phoneNumber: driverForm.phoneNumber.trim(),
        licensePlateNumber: driverForm.licensePlateNumber.trim(),
        vehicleColour: driverForm.vehicleColour.trim(),
        vehicleModel: driverForm.vehicleModel.trim() || undefined,
        acceptPrivacy: true
      };
      
      const response = await axios.post(`${getApiBase()}/auth/signup-driver`, payload);
      
      if (response.data.success) {
        setIsSubmitted(true);
        setDriverForm({
          email: '',
          name: '',
          phoneNumber: '',
          licensePlateNumber: '',
          vehicleColour: '',
          vehicleModel: '',
          acceptPrivacy: false
        });
      } else {
        setError(response.data.error || 'Failed to create account');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create account. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign Up - Boulevard</title>
        <meta name="description" content="Create your Boulevard account as a rider or driver. Join our zero-fee ride-sharing platform." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Create Your Account
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join Boulevard and start your journey as a rider or driver. Zero fees, complete freedom.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Signup Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Role Toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-white rounded-lg p-1 shadow-md border border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setRole('rider');
                  setError('');
                  setIsSubmitted(false);
                }}
                className={`px-6 py-3 rounded-md font-medium transition-all duration-200 flex items-center space-x-2 ${
                  role === 'rider'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                <FaUser />
                <span>Rider</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setRole('driver');
                  setError('');
                  setIsSubmitted(false);
                }}
                className={`px-6 py-3 rounded-md font-medium transition-all duration-200 flex items-center space-x-2 ${
                  role === 'driver'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                <FaCar />
                <span>Driver</span>
              </button>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8"
          >
            {isSubmitted ? (
              <div className="text-center py-12">
                <FaCheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Account Created Successfully!
                </h3>
                <p className="text-gray-600 mb-6">
                  Your {role} account has been created. You can now log in using your email address.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href={role === 'rider' ? 'https://t.me/Rideatease_bot' : 'https://t.me/Rideeasedriver_bot'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex items-center justify-center space-x-2"
                  >
                    <FaTelegram />
                    <span>Open {role === 'rider' ? 'Rider' : 'Driver'} App</span>
                  </a>
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setError('');
                    }}
                    className="btn-secondary"
                  >
                    Create Another Account
                  </button>
                </div>
              </div>
            ) : (
              <>
                {role === 'rider' ? (
                  <form onSubmit={handleRiderSubmit} className="space-y-6">
                    <div className="mb-6">
                      <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
                        Rider Registration
                      </h2>
                      <p className="text-gray-600">
                        Sign up to request rides and connect with drivers in your area.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="rider-email" className="block text-sm font-medium text-gray-700 mb-2">
                          <FaEnvelope className="inline mr-2" />
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="rider-email"
                          name="email"
                          value={riderForm.email}
                          onChange={handleRiderChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="your.email@example.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="rider-name" className="block text-sm font-medium text-gray-700 mb-2">
                          <FaUser className="inline mr-2" />
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="rider-name"
                          name="name"
                          value={riderForm.name}
                          onChange={handleRiderChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="rider-phone" className="block text-sm font-medium text-gray-700 mb-2">
                        <FaPhone className="inline mr-2" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="rider-phone"
                        name="phoneNumber"
                        value={riderForm.phoneNumber}
                        onChange={handleRiderChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="rider-home" className="block text-sm font-medium text-gray-700 mb-2">
                          <FaHome className="inline mr-2" />
                          Home Address
                        </label>
                        <input
                          type="text"
                          id="rider-home"
                          name="homeAddress"
                          value={riderForm.homeAddress}
                          onChange={handleRiderChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="123 Main St, City, State"
                        />
                      </div>

                      <div>
                        <label htmlFor="rider-work" className="block text-sm font-medium text-gray-700 mb-2">
                          <FaBriefcase className="inline mr-2" />
                          Work Address
                        </label>
                        <input
                          type="text"
                          id="rider-work"
                          name="workAddress"
                          value={riderForm.workAddress}
                          onChange={handleRiderChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="456 Business Ave, City, State"
                        />
                      </div>
                    </div>

                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="rider-privacy"
                        name="acceptPrivacy"
                        checked={riderForm.acceptPrivacy}
                        onChange={handleRiderChange}
                        required
                        className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="rider-privacy" className="text-sm text-gray-700">
                        I accept the <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-primary-600 hover:underline">Terms of Service</Link> *
                      </label>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-300 rounded-lg p-4 flex items-start">
                        <FaExclamationCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-red-700">{error}</p>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Creating Account...</span>
                          </>
                        ) : (
                          <>
                            <FaCheckCircle />
                            <span>Create Rider Account</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleDriverSubmit} className="space-y-6">
                    <div className="mb-6">
                      <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
                        Driver Registration
                      </h2>
                      <p className="text-gray-600">
                        Sign up to offer rides and earn money with zero platform fees.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="driver-email" className="block text-sm font-medium text-gray-700 mb-2">
                          <FaEnvelope className="inline mr-2" />
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="driver-email"
                          name="email"
                          value={driverForm.email}
                          onChange={handleDriverChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="your.email@example.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="driver-name" className="block text-sm font-medium text-gray-700 mb-2">
                          <FaUser className="inline mr-2" />
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="driver-name"
                          name="name"
                          value={driverForm.name}
                          onChange={handleDriverChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="driver-phone" className="block text-sm font-medium text-gray-700 mb-2">
                        <FaPhone className="inline mr-2" />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="driver-phone"
                        name="phoneNumber"
                        value={driverForm.phoneNumber}
                        onChange={handleDriverChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="driver-license" className="block text-sm font-medium text-gray-700 mb-2">
                          <FaIdCard className="inline mr-2" />
                          License Plate Number *
                        </label>
                        <input
                          type="text"
                          id="driver-license"
                          name="licensePlateNumber"
                          value={driverForm.licensePlateNumber}
                          onChange={handleDriverChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="ABC-1234"
                        />
                      </div>

                      <div>
                        <label htmlFor="driver-colour" className="block text-sm font-medium text-gray-700 mb-2">
                          <FaPalette className="inline mr-2" />
                          Vehicle Colour *
                        </label>
                        <input
                          type="text"
                          id="driver-colour"
                          name="vehicleColour"
                          value={driverForm.vehicleColour}
                          onChange={handleDriverChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          placeholder="Blue"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="driver-model" className="block text-sm font-medium text-gray-700 mb-2">
                        <FaCar className="inline mr-2" />
                        Vehicle Model
                      </label>
                      <input
                        type="text"
                        id="driver-model"
                        name="vehicleModel"
                        value={driverForm.vehicleModel}
                        onChange={handleDriverChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder="Toyota Camry 2020"
                      />
                    </div>

                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="driver-privacy"
                        name="acceptPrivacy"
                        checked={driverForm.acceptPrivacy}
                        onChange={handleDriverChange}
                        required
                        className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="driver-privacy" className="text-sm text-gray-700">
                        I accept the <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link> and <Link to="/terms" className="text-primary-600 hover:underline">Terms of Service</Link> *
                      </label>
                    </div>

                    {error && (
                      <div className="bg-red-50 border border-red-300 rounded-lg p-4 flex items-start">
                        <FaExclamationCircle className="text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-red-700">{error}</p>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Creating Account...</span>
                          </>
                        ) : (
                          <>
                            <FaCheckCircle />
                            <span>Create Driver Account</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Signup;
